/**
 * Pricing & Spares Tab (May 2026 — iteration 3).
 *
 * Draft-only line-item editing — Add / Delete / Edit do NOT touch the
 * backend. Every mutation bubbles into the modal's `draft.spare_parts`
 * array. The footer "Save All Changes" button (in ServiceOpsDetailModal)
 * flushes the array via PUT /service-ops/{id}/spare-parts and then upserts
 * every part row into spare_parts_master.
 *
 * Master-driven defaults:
 *   - Part-name typeahead resolves price / GST / labor / HSN from the
 *     master, scoped to the current car (make+model from lead.vehicle).
 *   - Brand typeahead lists existing master brands; arbitrary new brands
 *     are accepted (they'll mint a new master row on Save All Changes).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Loader2, Package as PackageIcon, ArrowRight,
  ArrowUp, ArrowDown, Wrench, CheckCircle2, Pencil, ChevronUp,
  Hammer, CreditCard, Copy, ExternalLink, XCircle, Activity as ActivityIcon,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  sparePartsMasterApi, servicePackagesApi, serviceOpsApi,
} from "../../../services/api";
import { formatCurrency } from "../../../utils/currency";
import { formatDateTime } from "../../../utils/datetime";

const DEFAULT_LABOR_RATE = 300;
const DEFAULT_GST_PCT = 18;
const DEFAULT_HSN_PART = "8708";
const DEFAULT_HSN_LABOR = "9987";

const mkId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const Card = ({ title, action, children, testid }) => (
  <div className="border rounded-lg overflow-hidden bg-white shadow-sm" data-testid={testid}>
    <div className="bg-slate-50 px-4 py-2.5 border-b flex items-center justify-between">
      <h3 className="text-sm font-semibold text-slate-700 tracking-tight">{title}</h3>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const SubtotalRow = ({ label, amount, user, tone = "", testid }) => (
  <div
    data-testid={testid}
    className={`flex items-center justify-between bg-slate-50 px-4 py-2 border-t text-sm ${tone}`}
  >
    <span className="font-medium text-slate-700">{label}</span>
    <span className="font-semibold tabular-nums">{formatCurrency(amount, user)}</span>
  </div>
);

// Local compute mirror of backend _compute_part_totals so the UI shows
// live totals while the user edits.
const computeTotals = (p) => {
  const qty = Number(p.qty || 1);
  const up = Number(p.unit_price || 0);
  const disc = Number(p.discount_pct || 0);
  const gst = Number(p.gst_pct ?? DEFAULT_GST_PCT);
  const subtotal = +(qty * up).toFixed(2);
  const discount_amount = +(subtotal * disc / 100).toFixed(2);
  const taxable = +(subtotal - discount_amount).toFixed(2);
  const gst_amount = +(taxable * gst / 100).toFixed(2);
  const total = +(taxable + gst_amount).toFixed(2);
  return { ...p, subtotal, discount_amount, taxable, gst_amount, total };
};

const emptyAddDraft = () => ({
  name: "", brand: "", qty: 1, unit_price: 0, discount_pct: 0,
  gst_pct: DEFAULT_GST_PCT, hsn_code: DEFAULT_HSN_PART, labor_amount: DEFAULT_LABOR_RATE,
});

export default function PricingSparesTab({ lead, user, draft, setDraft, onChanged }) {
  const veh = lead?.vehicle || {};
  const carMake = veh.brand || "";
  const carModel = veh.model || "";

  // ─── Packages ────────────────────────────────────────────────────────
  const [packages, setPackages] = useState([]);
  const [showSwitch, setShowSwitch] = useState(false);

  useEffect(() => {
    // Backend filters on `is_active` (bool) — the legacy `status: "active"`
    // we used to send was silently ignored, so archived packages kept
    // surfacing in the Pricing & Spares dropdown after a few hours when
    // the modal re-mounted.  CEO-reported 2026-05-23.
    servicePackagesApi
      .list({ is_active: true, page_size: 200 })
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : (data?.items || data?.packages || []);
        setPackages(list);
      })
      .catch((e) => console.warn("[PricingSparesTab] packages load failed:", e));
  }, []);

  const d = draft || {};
  const patch = (changes) => setDraft({ ...d, ...changes });

  const resolvePrice = (pkg) => {
    if (!pkg) return null;
    const rules = pkg.pricing || [];
    const match = rules.find((r) =>
      (!r.brand || (r.brand || "").toLowerCase() === carMake.toLowerCase()) &&
      (!r.fuel_type || (r.fuel_type || "").toLowerCase() === (veh.fuel_type || "").toLowerCase())
    );
    return match?.price ?? pkg.base_price ?? null;
  };

  const currentPkg = useMemo(
    () => packages.find((p) => p.id === lead?.package_id),
    [packages, lead?.package_id]
  );
  const newPkgId = d.package_id || lead?.package_id || "";
  const newPkg = packages.find((p) => p.id === newPkgId);
  const newPkgPrice = resolvePrice(newPkg);
  const currentPrice = Number(lead?.final_price || 0);
  const priceDelta = newPkg && newPkg.id !== lead?.package_id
    ? (newPkgPrice ?? 0) - currentPrice
    : 0;
  const isSwitching = newPkg && newPkg.id !== lead?.package_id;
  const packageSubtotal = isSwitching && newPkgPrice != null ? newPkgPrice : currentPrice;
  const pkgItems = (isSwitching ? newPkg?.items : currentPkg?.items) || [];

  // ─── Spare Parts — local draft state ─────────────────────────────────
  // Seeded from lead.spare_parts on every lead change. As soon as the user
  // mutates anything, the array is pushed up into draft.spare_parts so the
  // modal lights "Save All Changes" and persists on click.
  const [parts, setParts] = useState(() => (lead?.spare_parts || []).map(computeTotals));

  // Reseed when the lead reloads (after Save All) — but only if the parent
  // draft no longer holds spare_parts (i.e. we're not in mid-edit).
  useEffect(() => {
    if (!d.spare_parts) {
      setParts((lead?.spare_parts || []).map(computeTotals));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?.spare_parts]);

  const pushPartsDraft = (next) => {
    setParts(next);
    patch({ spare_parts: next });
  };

  const onCellChange = (id, field, raw) => {
    const next = parts.map((p) => {
      if (p.id !== id) return p;
      const v = ["qty", "unit_price", "discount_pct", "gst_pct"].includes(field)
        ? (raw === "" ? "" : Number(raw))
        : raw;
      return computeTotals({ ...p, [field]: v });
    });
    pushPartsDraft(next);
  };

  const onDeleteRow = (id) => {
    pushPartsDraft(parts.filter((p) => p.id !== id));
  };

  // ─── Add Part form (with brand + name autocomplete) ──────────────────
  const [addDraft, setAddDraft] = useState(emptyAddDraft());
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  // Name autocomplete — scoped to the current car (make+model) when present
  useEffect(() => {
    if (!addDraft.name || addDraft.name.length < 2) {
      setNameSuggestions([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await sparePartsMasterApi.search({
          q: addDraft.name,
          make: carMake || undefined,
          limit: 8,
        });
        if (!cancelled) setNameSuggestions(data?.items || []);
      } catch {
        if (!cancelled) setNameSuggestions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [addDraft.name, carMake]);

  // Brand autocomplete — distinct brand list from master, filtered by query
  useEffect(() => {
    if (!addDraft.brand || addDraft.brand.length < 1) {
      setBrandSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await sparePartsMasterApi.brands({ q: addDraft.brand, limit: 8 });
        if (!cancelled) {
          const exact = (data?.items || []).some(
            (b) => b.toLowerCase() === addDraft.brand.toLowerCase()
          );
          // Filter out exact match — no point suggesting what's already typed
          setBrandSuggestions(
            (data?.items || []).filter((b) => b.toLowerCase() !== addDraft.brand.toLowerCase())
              .concat(exact ? [] : [`__new__:${addDraft.brand}`])
          );
        }
      } catch {
        if (!cancelled) setBrandSuggestions([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [addDraft.brand]);

  const onAddPart = () => {
    const name = addDraft.name?.trim();
    if (!name || !addDraft.unit_price || Number(addDraft.unit_price) <= 0) {
      toast.error("Name and unit price are required");
      return;
    }
    const partRow = computeTotals({
      id: mkId("sp"),
      name,
      brand: addDraft.brand?.trim() || null,
      make: carMake || null,
      model: carModel || null,
      qty: Number(addDraft.qty || 1),
      unit_price: Number(addDraft.unit_price),
      discount_pct: Number(addDraft.discount_pct || 0),
      gst_pct: Number(addDraft.gst_pct ?? DEFAULT_GST_PCT),
      hsn_code: addDraft.hsn_code || DEFAULT_HSN_PART,
      type: "part",
    });
    const laborAmount = Number(addDraft.labor_amount ?? DEFAULT_LABOR_RATE);
    const laborRow = computeTotals({
      id: mkId("sp"),
      name: `Labor — ${name}`,
      brand: null,
      make: carMake || null,
      model: carModel || null,
      qty: 1,
      unit_price: laborAmount,
      discount_pct: 0,
      gst_pct: Number(addDraft.gst_pct ?? DEFAULT_GST_PCT),
      hsn_code: DEFAULT_HSN_LABOR,
      type: "labor",
      // Bind the labor row to the part by name so the master-upsert step
      // can fold its amount back into the part's master row.
      labor_for: partRow.id,
    });
    pushPartsDraft([...parts, partRow, laborRow]);
    setAddDraft(emptyAddDraft());
    setNameSuggestions([]);
    setBrandSuggestions([]);
    toast.success("Part + Labor staged · click Save All Changes to persist");
  };

  const fillFromSuggestion = (s) => {
    setAddDraft((dd) => ({
      ...dd,
      name: s.name,
      brand: s.brand || "",
      unit_price: s.resolved_price ?? s.default_price ?? 0,
      hsn_code: s.hsn_code || DEFAULT_HSN_PART,
      gst_pct: s.gst_pct ?? DEFAULT_GST_PCT,
      labor_amount: s.labor_amount ?? DEFAULT_LABOR_RATE,
    }));
    setNameSuggestions([]);
    toast.info(
      `Loaded from master · ${activeCurrencySymbol()}${s.resolved_price ?? s.default_price ?? "—"} + ` +
      `Labor ${activeCurrencySymbol()}${s.labor_amount ?? DEFAULT_LABOR_RATE}`
    );
  };

  const pickBrand = (b) => {
    const isNew = b.startsWith("__new__:");
    const value = isNew ? b.slice("__new__:".length) : b;
    setAddDraft((dd) => ({ ...dd, brand: value }));
    setBrandSuggestions([]);
    if (isNew) toast.info(`Brand "${value}" will be added to master on Save All`);
  };

  // ─── Totals ──────────────────────────────────────────────────────────
  const partRows = parts.filter((p) => (p.type || "part") !== "labor");
  const laborRows = parts.filter((p) => (p.type || "part") === "labor");
  // Each part's "all-in" total already includes its GST and post-discount value.
  const partsAllInTotal = partRows.reduce((s, p) => s + Number(p.total || 0), 0);
  const laborAllInTotal = laborRows.reduce((s, p) => s + Number(p.total || 0), 0);
  // Pre-GST taxable + GST split so the breakdown can show GST as its own line.
  const partsLaborTaxable = parts.reduce((s, p) => s + Number(p.taxable || 0), 0);
  const partsLaborGst = parts.reduce((s, p) => s + Number(p.gst_amount || 0), 0);
  const partsLaborDiscount = parts.reduce((s, p) => s + Number(p.discount_amount || 0), 0);
  const partsOnlyTotal = partsAllInTotal;
  const laborTotal = laborAllInTotal;

  const grandTotal = packageSubtotal + partsAllInTotal + laborAllInTotal;
  const advancePaid = Number(lead?.advance_amount || 0);
  const addonsPaid = (lead?.additional_payment_links || [])
    .filter((l) => (l.payment_status || "").toUpperCase() === "PAID")
    .reduce((s, l) => s + Number(l.amount || 0), 0);
  const totalPaid = advancePaid + addonsPaid;
  const balanceDue = Math.max(0, grandTotal - totalPaid);

  // ── Inline cell renderers ────────────────────────────────────────────
  const cellInput = (p, field, props = {}) => (
    <Input
      type={props.type || "number"}
      min={props.min ?? 0}
      max={props.max}
      step={props.step || "any"}
      value={p[field] ?? ""}
      onChange={(e) => onCellChange(p.id, field, e.target.value)}
      className={`h-7 text-xs text-right ml-auto ${props.className || "w-16"}`}
      data-testid={`part-${field}-${p.id}`}
    />
  );

  // ─── Payments (merged in from old PaymentsTab) ───────────────────────
  const [linkMode, setLinkMode] = useState("separate");      // 'merge' | 'separate'
  const [linkAmount, setLinkAmount] = useState("");
  const [linkAmountEdited, setLinkAmountEdited] = useState(false);
  const [linkDescription, setLinkDescription] = useState("");
  const [linkWhatsapp, setLinkWhatsapp] = useState(true);
  const [linkBusy, setLinkBusy] = useState(false);

  // ─── Outstanding by category (booking vs spares + labour) ────────────
  // Track how much of each bucket the customer has settled. Order of
  // allocation: advance + booking-link payments go to the booking total
  // first (capped at final_price); add-on payments split by link mode —
  // `separate` always lands on the spares bucket, `merge` payments cover
  // booking remainder first, then spill into spares.
  const bookingTotal = Number(lead?.final_price || 0);
  const sparesLabourTotal = partsAllInTotal + laborAllInTotal;

  let bookingPaid = Math.min(advancePaid, bookingTotal);
  let sparesPaid = 0;
  for (const link of (lead?.additional_payment_links || [])) {
    if ((link.payment_status || "").toUpperCase() !== "PAID") continue;
    const amt = Number(link.amount || 0);
    if (link.mode === "merge") {
      const goesToBooking = Math.max(0, Math.min(amt, bookingTotal - bookingPaid));
      bookingPaid += goesToBooking;
      sparesPaid += amt - goesToBooking;
    } else {
      sparesPaid += amt;
    }
  }
  const bookingOutstanding = Math.max(0, bookingTotal - bookingPaid);
  const sparesOutstanding = Math.max(0, sparesLabourTotal - sparesPaid);
  const totalOutstanding = bookingOutstanding + sparesOutstanding;

  // Default amount for the new link — auto-updates whenever the mode flips
  // or the underlying outstanding changes. The rep can still override it
  // by typing — we track `linkAmountEdited` so we don't clobber their edit.
  const defaultLinkAmount =
    linkMode === "merge" ? totalOutstanding : sparesOutstanding;

  useEffect(() => {
    if (!linkAmountEdited) {
      setLinkAmount(defaultLinkAmount ? String(defaultLinkAmount.toFixed(2)) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkMode, defaultLinkAmount]);

  // Override panel — preset to current status whenever lead reloads
  const [showOverride, setShowOverride] = useState(false);
  const [overrideKind, setOverrideKind] = useState("cash_balance"); // semantic preset
  const [overridePayload, setOverridePayload] = useState({
    payment_status: lead?.payment_status || "PARTIAL_PAID",
    amount_paid: "",
    note: "",
  });
  useEffect(() => {
    setOverridePayload((p) => ({
      ...p,
      payment_status: lead?.payment_status || "PARTIAL_PAID",
    }));
  }, [lead?.payment_status]);

  // Cancelled / paid links don't contribute to the "active" link list.
  const activeLinks = (lead?.additional_payment_links || []).filter(
    (l) => !["CANCELLED", "REFUNDED"].includes((l.payment_status || "").toUpperCase())
  );
  const cancelledLinks = (lead?.additional_payment_links || []).filter(
    (l) => (l.payment_status || "").toUpperCase() === "CANCELLED"
  );

  const onCreateLink = async () => {
    if (Object.keys(draft || {}).length > 0) {
      toast.error("You have unsaved changes — click Save All Changes first");
      return;
    }
    setLinkBusy(true);
    try {
      const res = await serviceOpsApi.createAdditionalLink(lead.id, {
        mode: linkMode,
        amount_override: linkAmount ? Number(linkAmount) : null,
        description: linkDescription,
        send_whatsapp: linkWhatsapp,
      });
      toast.success(`Link created · ${formatCurrency(res?.data?.link?.amount, user)}`);
      setLinkAmount("");
      setLinkAmountEdited(false);
      setLinkDescription("");
      onChanged?.();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setLinkBusy(false);
    }
  };

  const onCancelLink = async (linkId) => {
    if (!confirm("Cancel this payment link? The customer's existing link will be invalidated on Razorpay.")) return;
    try {
      await serviceOpsApi.cancelAdditionalLink(lead.id, linkId);
      toast.success("Link cancelled");
      onChanged?.();
    } catch (e) {
      toast.error("Cancel failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const onOverride = async () => {
    if (!overridePayload.note?.trim() || overridePayload.note.length < 2) {
      toast.error("Reason is required for manual override");
      return;
    }
    try {
      await serviceOpsApi.overridePaymentStatus(lead.id, {
        payment_status: overridePayload.payment_status,
        amount_paid: overridePayload.amount_paid
          ? Number(overridePayload.amount_paid) : null,
        note: overridePayload.note,
      });
      toast.success("Payment status updated");
      setShowOverride(false);
      onChanged?.();
    } catch (e) {
      toast.error("Override failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  // ─── Activity log filter — events relevant to pricing/spares/payments ─
  const PRICING_ACTIVITY_TYPES = new Set([
    "spare_part_added", "spare_part_updated", "spare_part_deleted",
    "spare_parts_bulk_replace",
    "additional_payment_link_sent", "additional_payment_link_cancelled",
    "payment_status_override", "package_changed", "quote_sent",
    "payment_link_sent", "payment_received", "invoice_generated", "invoice_sent",
  ]);
  const pricingActivities = (lead?.activities || [])
    .filter((a) => PRICING_ACTIVITY_TYPES.has(a.type))
    .slice()
    .reverse(); // newest first

  return (
    <div className="p-5 space-y-4" data-testid="pricing-spares-tab">
      {/* ───────── 1. Service Package ───────── */}
      <Card
        title="Service Package"
        testid="ps-package"
        action={
          <Button
            size="sm"
            variant={showSwitch ? "secondary" : "outline"}
            onClick={() => setShowSwitch((s) => !s)}
            className="h-7 text-xs"
            data-testid="package-toggle-switch-btn"
          >
            {showSwitch ? (
              <><ChevronUp className="h-3 w-3 mr-1" /> Hide switch</>
            ) : (
              <><Pencil className="h-3 w-3 mr-1" /> Change package</>
            )}
          </Button>
        }
      >
        <div className="grid grid-cols-12 gap-4 items-start" data-testid="pkg-current-summary">
          <div className="col-span-5 bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="pkg-current">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Current Package</div>
            <div className="font-semibold text-slate-900">{lead?.package_name || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{currentPkg?.category || ""}</div>
            <div className="text-lg font-bold text-slate-800 mt-2">{formatCurrency(currentPrice, user)}</div>
            <div className="text-xs text-slate-500 mt-2 space-y-0.5">
              <div>Advance Paid: <span className="font-medium text-emerald-700">{formatCurrency(advancePaid, user)}</span></div>
              <div>Balance: <span className="font-medium text-amber-700">{formatCurrency(lead?.balance_amount, user)}</span></div>
            </div>
          </div>

          <div className="col-span-7 bg-white rounded-lg p-3 border border-slate-200" data-testid="pkg-items-block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              What's covered ({pkgItems.length} item{pkgItems.length === 1 ? "" : "s"})
            </div>
            {pkgItems.length === 0 ? (
              <div className="text-xs text-slate-400 italic">No service items configured on this package.</div>
            ) : (
              <ul className="space-y-1 max-h-40 overflow-y-auto pr-2" data-testid="pkg-items-list">
                {pkgItems.map((it, idx) => (
                  <li key={it.id || idx} className="flex items-start gap-2 text-sm" data-testid={`pkg-item-${idx}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-medium text-slate-800">{it.name}</div>
                      {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {showSwitch && (
          <div className="mt-4 grid grid-cols-12 gap-4 items-stretch border-t pt-4" data-testid="pkg-switch-block">
            <div className="col-span-5 bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Current</div>
              <div className="font-semibold text-slate-900">{lead?.package_name || "—"}</div>
              <div className="text-lg font-bold text-slate-800 mt-2">{formatCurrency(currentPrice, user)}</div>
            </div>
            <div className="col-span-1 flex flex-col items-center justify-center" data-testid="pkg-diff">
              <ArrowRight className="h-6 w-6 text-slate-400 mb-1" />
              {isSwitching && newPkgPrice != null && (
                <div className={`text-xs font-bold tabular-nums px-2 py-1 rounded ${
                  priceDelta > 0 ? "bg-amber-100 text-amber-800"
                  : priceDelta < 0 ? "bg-emerald-100 text-emerald-800"
                  : "bg-slate-100 text-slate-700"
                }`}>
                  {priceDelta > 0 ? (<><ArrowUp className="h-3 w-3 inline" /> {formatCurrency(priceDelta, user)} more</>)
                  : priceDelta < 0 ? (<><ArrowDown className="h-3 w-3 inline" /> {formatCurrency(Math.abs(priceDelta), user)} less</>)
                  : "no change"}
                </div>
              )}
            </div>
            <div className={`col-span-6 rounded-lg p-3 border ${isSwitching ? "bg-blue-50 border-blue-300" : "bg-white border-dashed border-slate-300"}`} data-testid="pkg-new">
              <div className="text-xs uppercase tracking-wider text-blue-700 mb-2 flex items-center gap-1">
                <PackageIcon className="h-3.5 w-3.5" /> Switch to new package
              </div>
              <select
                value={newPkgId}
                onChange={(e) => patch({ package_id: e.target.value })}
                className="w-full border rounded-md px-3 py-2 text-sm mb-2 cursor-pointer hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="package-select"
              >
                <option value="">-- keep current --</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.category ? ` · ${p.category}` : ""}
                    {resolvePrice(p) != null ? ` — ${formatCurrency(resolvePrice(p), user)}` : ""}
                  </option>
                ))}
              </select>
              {isSwitching && (
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">{newPkg.name}</div>
                  {newPkgPrice != null && (
                    <div className="text-lg font-bold text-blue-700 mt-1">{formatCurrency(newPkgPrice, user)}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <SubtotalRow label="Service Package Subtotal" amount={packageSubtotal} user={user}
          tone="text-blue-800 bg-blue-50/60" testid="pkg-subtotal-row" />
      </Card>

      {/* ───────── 2. Spare Parts + Labor ───────── */}
      <Card title={`Spare Parts & Labor · ${carMake || "—"} ${carModel || ""}`.trim()} testid="ps-parts">
        <div className="space-y-3">
          <div className="border rounded-lg overflow-hidden" data-testid="parts-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-2 py-2 text-left">Type</th>
                  <th className="px-2 py-2 text-left">Item</th>
                  <th className="px-2 py-2 text-left">Brand</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-right">Unit</th>
                  <th className="px-2 py-2 text-right">Disc %</th>
                  <th className="px-2 py-2 text-right">GST %</th>
                  <th className="px-2 py-2 text-right">Total</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {parts.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-slate-400 py-8 italic">
                      <Wrench className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      No spare parts added yet.
                    </td>
                  </tr>
                )}
                {parts.map((p) => {
                  const isLabor = (p.type || "part") === "labor";
                  return (
                    <tr key={p.id} className={`border-t hover:bg-slate-50 ${isLabor ? "bg-amber-50/40" : ""}`} data-testid={`part-row-${p.id}`}>
                      <td className="px-2 py-1.5">
                        {isLabor ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">
                            <Hammer className="h-3 w-3" /> Labor
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-800 bg-blue-100 border border-blue-300 px-1.5 py-0.5 rounded">
                            <Wrench className="h-3 w-3" /> Part
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          value={p.name || ""}
                          onChange={(e) => onCellChange(p.id, "name", e.target.value)}
                          className="h-7 text-xs"
                          data-testid={`part-name-${p.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          value={p.brand || ""}
                          onChange={(e) => onCellChange(p.id, "brand", e.target.value)}
                          placeholder={isLabor ? "—" : "Bosch"}
                          className="h-7 text-xs"
                          data-testid={`part-brand-${p.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">{cellInput(p, "qty", { min: 0.01, className: "w-14" })}</td>
                      <td className="px-2 py-1.5">{cellInput(p, "unit_price", { className: "w-20" })}</td>
                      <td className="px-2 py-1.5">{cellInput(p, "discount_pct", { max: 100, className: "w-14" })}</td>
                      <td className="px-2 py-1.5">{cellInput(p, "gst_pct", { max: 100, className: "w-14" })}</td>
                      <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                        {formatCurrency(p.total, user)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" variant="ghost" onClick={() => onDeleteRow(p.id)} data-testid={`part-delete-${p.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Add part form */}
          <div className="border-2 border-dashed border-blue-300 bg-blue-50/40 rounded-lg p-3" data-testid="parts-add-form">
            <div className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add Spare Part
              <span className="text-xs font-normal text-slate-600">
                · pick from master to auto-fill price/GST/labor · staged until Save All Changes
              </span>
            </div>
            <div className="grid grid-cols-12 gap-2">
              {/* Part name */}
              <div className="col-span-3 relative">
                <Label className="text-xs">Part Name</Label>
                <Input
                  value={addDraft.name}
                  onChange={(e) => setAddDraft({ ...addDraft, name: e.target.value })}
                  placeholder="e.g. Brake Pad (Front)"
                  data-testid="part-name-input"
                />
                {searching && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-7 text-slate-400" />}
                {nameSuggestions.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                    {nameSuggestions.map((s) => (
                      <button
                        key={s.id} type="button" onClick={() => fillFromSuggestion(s)}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b last:border-0"
                        data-testid={`part-suggest-${s.id}`}
                      >
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-slate-500">
                          {s.brand || "—"}
                          {s.car_make ? ` · ${s.car_make}${s.car_model ? ` ${s.car_model}` : ""}` : ""}
                          {" · "}
                          {s.resolved_price ? formatCurrency(s.resolved_price, user) : "no price"}
                          {s.labor_amount ? ` + Labor ${formatCurrency(s.labor_amount, user)}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Brand */}
              <div className="col-span-2 relative">
                <Label className="text-xs">Brand</Label>
                <Input
                  value={addDraft.brand}
                  onChange={(e) => setAddDraft({ ...addDraft, brand: e.target.value })}
                  placeholder="Bosch / OEM"
                  data-testid="part-brand-input"
                />
                {brandSuggestions.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 bg-white border rounded shadow-lg max-h-40 overflow-y-auto">
                    {brandSuggestions.map((b, i) => {
                      const isNew = b.startsWith("__new__:");
                      const label = isNew ? b.slice("__new__:".length) : b;
                      return (
                        <button
                          key={i} type="button" onClick={() => pickBrand(b)}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b last:border-0"
                          data-testid={`brand-suggest-${i}`}
                        >
                          {isNew ? (
                            <span className="flex items-center justify-between">
                              <span className="font-medium">{label}</span>
                              <span className="text-xs text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded">
                                + Add new
                              </span>
                            </span>
                          ) : (
                            <span className="font-medium">{label}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="col-span-1">
                <Label className="text-xs">Qty</Label>
                <Input type="number" min={1} value={addDraft.qty}
                  onChange={(e) => setAddDraft({ ...addDraft, qty: e.target.value })}
                  data-testid="part-qty-input" />
              </div>
              <div className="col-span-1">
                <Label className="text-xs">Unit Price</Label>
                <Input type="number" min={0} value={addDraft.unit_price}
                  onChange={(e) => setAddDraft({ ...addDraft, unit_price: e.target.value })}
                  data-testid="part-price-input" />
              </div>
              <div className="col-span-1">
                <Label className="text-xs">Disc %</Label>
                <Input type="number" min={0} max={100} value={addDraft.discount_pct}
                  onChange={(e) => setAddDraft({ ...addDraft, discount_pct: e.target.value })}
                  data-testid="part-discount-input" />
              </div>
              <div className="col-span-1">
                <Label className="text-xs">GST %</Label>
                <Input type="number" min={0} max={100} value={addDraft.gst_pct}
                  onChange={(e) => setAddDraft({ ...addDraft, gst_pct: e.target.value })}
                  data-testid="part-gst-input" />
              </div>
              <div className="col-span-1">
                <Label className="text-xs">Labor</Label>
                <Input type="number" min={0} value={addDraft.labor_amount}
                  onChange={(e) => setAddDraft({ ...addDraft, labor_amount: e.target.value })}
                  data-testid="part-labor-input" />
              </div>
              <div className="col-span-2 flex items-end">
                <Button onClick={onAddPart} disabled={!addDraft.name || !addDraft.unit_price}
                  className="w-full" data-testid="part-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> Stage Part + Labor
                </Button>
              </div>
            </div>
          </div>
        </div>

        <SubtotalRow label="Spare Parts Subtotal (parts only, incl. GST)" amount={partsOnlyTotal} user={user}
          tone="text-blue-800 bg-blue-50/60" testid="parts-subtotal-row" />
        <SubtotalRow label="Labor Subtotal (incl. GST)" amount={laborTotal} user={user}
          tone="text-amber-800 bg-amber-50/60" testid="labor-subtotal-row" />
      </Card>

      {/* ───────── 3. Job Total Breakdown ───────── */}
      <div className="border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg overflow-hidden" data-testid="ps-grand">
        <div className="px-4 py-3 border-b border-emerald-200">
          <div className="text-xs uppercase tracking-wider text-emerald-800 font-bold">Job Total Breakdown</div>
        </div>
        <div className="divide-y divide-emerald-100">
          {/* 1. Service Package */}
          <div className="px-4 py-2 flex items-center justify-between text-sm" data-testid="grand-row-package">
            <span className="text-slate-700 font-medium">Service Package — {lead?.package_name || "—"}</span>
            <span className="font-semibold tabular-nums">{formatCurrency(packageSubtotal, user)}</span>
          </div>

          {/* 2. Each spare part itemised */}
          {partRows.length > 0 && (
            <div className="px-4 py-2 bg-blue-50/40">
              <div className="text-xs text-slate-500 font-semibold mb-1">Spare Parts</div>
              <div className="space-y-0.5" data-testid="grand-parts-list">
                {partRows.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs pl-2" data-testid={`grand-part-${p.id}`}>
                    <span className="text-slate-700">
                      <span className="inline-block w-4 text-slate-400">·</span>
                      {p.name}{p.brand ? ` (${p.brand})` : ""}{p.qty > 1 ? ` × ${p.qty}` : ""}
                    </span>
                    <span className="tabular-nums text-slate-700">{formatCurrency(p.total, user)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. Labour total */}
          <div className="px-4 py-2 flex items-center justify-between text-sm" data-testid="grand-row-labor">
            <span className="text-slate-700">Labour charge (total)</span>
            <span className="font-semibold tabular-nums">{formatCurrency(laborTotal, user)}</span>
          </div>

          {/* 4. GST */}
          <div className="px-4 py-2 flex items-center justify-between text-sm" data-testid="grand-row-gst">
            <span className="text-slate-700">GST (on parts + labour)</span>
            <span className="font-semibold tabular-nums text-slate-700">{formatCurrency(partsLaborGst, user)}</span>
          </div>

          {/* 5. Discount */}
          <div className="px-4 py-2 flex items-center justify-between text-sm" data-testid="grand-row-discount">
            <span className="text-slate-700">Discount</span>
            <span className="font-semibold tabular-nums text-amber-700">
              {partsLaborDiscount > 0 ? "− " : ""}{formatCurrency(partsLaborDiscount, user)}
            </span>
          </div>

          {/* Grand total */}
          <div className="px-4 py-3 flex items-center justify-between bg-emerald-100/60" data-testid="grand-row-total">
            <span className="text-emerald-900 font-bold text-base">GRAND TOTAL</span>
            <span className="text-emerald-900 font-bold text-xl tabular-nums">{formatCurrency(grandTotal, user)}</span>
          </div>

          {/* 6. Advance paid */}
          <div className="px-4 py-2 flex items-center justify-between text-sm" data-testid="grand-row-advance">
            <span className="text-slate-700">Advance Paid</span>
            <span className="font-medium tabular-nums text-emerald-700">− {formatCurrency(advancePaid, user)}</span>
          </div>
          {addonsPaid > 0 && (
            <div className="px-4 py-2 flex items-center justify-between text-sm" data-testid="grand-row-addons">
              <span className="text-slate-700">Add-on payments received</span>
              <span className="font-medium tabular-nums text-emerald-700">− {formatCurrency(addonsPaid, user)}</span>
            </div>
          )}

          {/* 7. Remaining balance */}
          <div className="px-4 py-3 flex items-center justify-between bg-amber-50" data-testid="grand-row-balance">
            <span className="text-slate-900 font-bold">Remaining Balance</span>
            <span className={`font-bold tabular-nums text-lg ${balanceDue > 0 ? "text-amber-700" : "text-emerald-700"}`}>
              {formatCurrency(balanceDue, user)}
            </span>
          </div>
        </div>
      </div>

      {/* ───────── 4. Payments — merged from old Payments tab ───────── */}
      <Card title="Payments" testid="ps-payments">
        <div className="space-y-3">
          {/* Outstanding by category — booking vs spares */}
          <div className="grid grid-cols-2 gap-3" data-testid="outstanding-by-category">
            <div className={`border-2 rounded-lg p-3 ${bookingOutstanding > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="outstanding-booking">
              <div className="text-xs uppercase tracking-wider text-slate-600 mb-1 flex items-center gap-1">
                <PackageIcon className="h-3.5 w-3.5" /> Booking Package
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Paid</div>
                  <div className="font-semibold text-emerald-700 tabular-nums">{formatCurrency(bookingPaid, user)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Outstanding</div>
                  <div className={`font-bold text-base tabular-nums ${bookingOutstanding > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                    {formatCurrency(bookingOutstanding, user)}
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">of {formatCurrency(bookingTotal, user)}</div>
            </div>
            <div className={`border-2 rounded-lg p-3 ${sparesOutstanding > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="outstanding-spares">
              <div className="text-xs uppercase tracking-wider text-slate-600 mb-1 flex items-center gap-1">
                <Wrench className="h-3.5 w-3.5" /> Spare Parts + Labour
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Paid</div>
                  <div className="font-semibold text-emerald-700 tabular-nums">{formatCurrency(sparesPaid, user)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Outstanding</div>
                  <div className={`font-bold text-base tabular-nums ${sparesOutstanding > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                    {formatCurrency(sparesOutstanding, user)}
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">of {formatCurrency(sparesLabourTotal, user)}</div>
            </div>
          </div>

          {/* Total outstanding warning bar */}
          {totalOutstanding > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded px-3 py-2 text-sm flex items-center justify-between" data-testid="total-outstanding-bar">
              <span className="text-amber-900 font-semibold">
                ⚠️ Total still to collect from customer
              </span>
              <span className="font-bold tabular-nums text-amber-900">{formatCurrency(totalOutstanding, user)}</span>
            </div>
          )}
          {totalOutstanding === 0 && grandTotal > 0 && (
            <div className="bg-emerald-50 border border-emerald-300 rounded px-3 py-2 text-sm flex items-center justify-between" data-testid="fully-paid-bar">
              <span className="text-emerald-900 font-semibold">✓ Customer has paid in full</span>
              <span className="font-bold tabular-nums text-emerald-900">{formatCurrency(grandTotal, user)}</span>
            </div>
          )}

          {/* Create new payment link */}
          <div className="border-2 border-dashed border-amber-300 bg-amber-50/40 rounded-lg p-3" data-testid="ps-create-link">
            <div className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <Plus className="h-4 w-4" /> Send Payment Link
              <span className="text-xs font-normal text-slate-600">
                · pick what's being collected — amount auto-fills
              </span>
            </div>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4">
                <Label className="text-xs">What is the customer paying?</Label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                  value={linkMode}
                  onChange={(e) => { setLinkMode(e.target.value); setLinkAmountEdited(false); }}
                  data-testid="link-mode-select"
                >
                  <option value="separate">Spare parts + labour only — {formatCurrency(sparesOutstanding, user)}</option>
                  <option value="merge">Everything still due (booking + spares) — {formatCurrency(totalOutstanding, user)}</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Amount</Label>
                <Input type="number" value={linkAmount}
                  onChange={(e) => { setLinkAmount(e.target.value); setLinkAmountEdited(true); }}
                  placeholder={String(defaultLinkAmount.toFixed(2))}
                  data-testid="link-amount-input" />
              </div>
              <div className="col-span-4">
                <Label className="text-xs">Description</Label>
                <Input value={linkDescription}
                  onChange={(e) => setLinkDescription(e.target.value)}
                  placeholder="e.g. Brake pads & labour"
                  data-testid="link-description-input" />
              </div>
              <div className="col-span-2 flex items-end">
                <Button onClick={onCreateLink} disabled={linkBusy || defaultLinkAmount <= 0} className="w-full" data-testid="link-create-btn">
                  <CreditCard className="h-4 w-4 mr-1.5" />
                  {linkBusy ? "Creating…" : "Send Link"}
                </Button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 mt-2">
              <input type="checkbox" checked={linkWhatsapp}
                onChange={(e) => setLinkWhatsapp(e.target.checked)}
                data-testid="link-whatsapp-toggle" />
              Send link via WhatsApp to {lead?.mobile || "customer"}
            </label>
          </div>

          {/* Active links */}
          {activeLinks.length === 0 && cancelledLinks.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No additional payment links yet.</div>
          ) : (
            <div className="space-y-2" data-testid="ps-links-list">
              {activeLinks.map((link) => {
                const status = (link.payment_status || "PENDING").toUpperCase();
                const canCancel = !["PAID", "REFUNDED", "CANCELLED"].includes(status);
                const tone = {
                  PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
                  PENDING: "bg-slate-100 text-slate-700 border-slate-200",
                  LINK_SENT: "bg-indigo-100 text-indigo-700 border-indigo-200",
                  PARTIAL_PAID: "bg-amber-100 text-amber-700 border-amber-200",
                }[status] || "bg-slate-100 text-slate-700 border-slate-200";
                const modeLabel = link.mode === "merge" ? "covers booking + spares" : "spares only";
                return (
                  <div key={link.id} className="bg-white border rounded p-2.5 flex items-center justify-between gap-3" data-testid={`link-row-${link.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{formatCurrency(link.amount, user)}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${tone}`}>{status}</span>
                        <span className="text-xs text-slate-500 italic">{modeLabel}</span>
                        <span className="text-xs text-slate-400">
                          {link.created_at ? `· created ${formatDateTime(link.created_at)}` : ""}
                        </span>
                      </div>
                      {link.description && (
                        <div className="text-xs text-slate-600 truncate mt-0.5">{link.description}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {link.razorpay_link_url && (
                        <>
                          <Button size="sm" variant="outline" title="Copy link"
                            onClick={() => {
                              navigator.clipboard.writeText(link.razorpay_link_url);
                              toast.success("Link copied");
                            }} data-testid={`link-copy-${link.id}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" title="Open"
                            onClick={() => window.open(link.razorpay_link_url, "_blank", "noopener")}
                            data-testid={`link-open-${link.id}`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {canCancel && (
                        <Button size="sm" variant="outline" title="Cancel link"
                          onClick={() => onCancelLink(link.id)}
                          className="text-red-600 hover:bg-red-50 border-red-200"
                          data-testid={`link-cancel-${link.id}`}>
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {cancelledLinks.map((link) => (
                <div key={link.id} className="bg-slate-50 border border-dashed rounded p-2 flex items-center justify-between gap-3 opacity-70" data-testid={`link-cancelled-${link.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm line-through text-slate-500">{formatCurrency(link.amount, user)}</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border bg-rose-100 text-rose-700 border-rose-200">CANCELLED</span>
                      <span className="text-xs text-slate-400">
                        {link.cancelled_at ? `· ${formatDateTime(link.cancelled_at)}` : ""}
                      </span>
                    </div>
                    {link.description && (
                      <div className="text-xs text-slate-500 truncate mt-0.5">{link.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Record outside payment (cash, UPI to mechanic, bank transfer) */}
          <div className="border-t pt-3">
            <Button size="sm" variant="outline" onClick={() => setShowOverride((v) => !v)} data-testid="override-toggle-btn">
              <CreditCard className="h-3.5 w-3.5 mr-1.5" />
              Record outside payment (cash / UPI / bank transfer)
            </Button>
            {showOverride && (
              <div className="mt-3 border rounded p-3 space-y-3 bg-slate-50" data-testid="override-form">
                <div className="text-xs text-slate-600 italic">
                  Use this only when the customer pays <strong>outside Razorpay</strong> — e.g. cash on-site,
                  direct UPI to the mechanic, bank transfer. Razorpay payments auto-reconcile via webhooks; you
                  don't need to touch this for them.
                </div>

                <div>
                  <Label className="text-xs">What did you collect?</Label>
                  <select
                    className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                    value={overrideKind}
                    onChange={(e) => {
                      const k = e.target.value;
                      setOverrideKind(k);
                      if (k === "cash_balance_full") {
                        setOverridePayload((p) => ({
                          ...p, payment_status: "PAID", amount_paid: String(totalOutstanding.toFixed(2)),
                        }));
                      } else if (k === "cash_spares_only") {
                        setOverridePayload((p) => ({
                          ...p, payment_status: "PARTIAL_PAID", amount_paid: String(sparesOutstanding.toFixed(2)),
                        }));
                      } else if (k === "cash_booking_only") {
                        setOverridePayload((p) => ({
                          ...p, payment_status: "PARTIAL_PAID", amount_paid: String(bookingOutstanding.toFixed(2)),
                        }));
                      } else if (k === "refund_issued") {
                        setOverridePayload((p) => ({
                          ...p, payment_status: "REFUNDED", amount_paid: "",
                        }));
                      }
                    }}
                    data-testid="override-kind-select"
                  >
                    <option value="cash_balance_full">Customer paid full outstanding ({formatCurrency(totalOutstanding, user)})</option>
                    <option value="cash_spares_only">Customer paid spare parts + labour only ({formatCurrency(sparesOutstanding, user)})</option>
                    <option value="cash_booking_only">Customer paid booking balance only ({formatCurrency(bookingOutstanding, user)})</option>
                    <option value="refund_issued">Refund issued to customer</option>
                    <option value="custom">Custom — set status &amp; amount manually</option>
                  </select>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">New Payment Status</Label>
                    <select
                      className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                      value={overridePayload.payment_status}
                      onChange={(e) => setOverridePayload({ ...overridePayload, payment_status: e.target.value })}
                      data-testid="override-status-select"
                      disabled={overrideKind !== "custom"}
                    >
                      {["PENDING", "LINK_SENT", "PARTIAL_PAID", "ADVANCE_PAID", "PAID", "REFUNDED", "FAILED"].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Amount Paid (cumulative)</Label>
                    <Input type="number" value={overridePayload.amount_paid}
                      onChange={(e) => setOverridePayload({ ...overridePayload, amount_paid: e.target.value })}
                      data-testid="override-amount-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Payment method (for audit)</Label>
                    <select
                      className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                      onChange={(e) => setOverridePayload((p) => ({
                        ...p, note: e.target.value ? `${e.target.value}${p.note?.replace(/^[A-Z]+:\s*/, "") ? " — " + p.note.replace(/^[A-Z]+:\s*/, "") : ""}` : p.note,
                      }))}
                      defaultValue=""
                      data-testid="override-method-select"
                    >
                      <option value="">Select…</option>
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI (direct to mechanic)</option>
                      <option value="BANK_TRANSFER">Bank transfer</option>
                      <option value="CHEQUE">Cheque</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Reason / receipt note (audit-logged)</Label>
                  <Input value={overridePayload.note}
                    onChange={(e) => setOverridePayload({ ...overridePayload, note: e.target.value })}
                    placeholder="e.g. CASH — Receipt #1234 from on-site mechanic"
                    data-testid="override-note-input" />
                </div>

                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowOverride(false)}>Cancel</Button>
                  <Button size="sm" onClick={onOverride} data-testid="override-save-btn">Record Payment</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ───────── 5. Activity Log ───────── */}
      <Card title="Pricing & Payments Activity" testid="ps-activity">
        {pricingActivities.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No activity logged for this lead yet.</div>
        ) : (
          <ul className="divide-y max-h-72 overflow-y-auto" data-testid="ps-activity-list">
            {pricingActivities.map((a) => {
              const meta = ACTIVITY_META[a.type] || { icon: ActivityIcon, color: "text-slate-500", label: a.type };
              const Icon = meta.icon;
              return (
                <li key={a.id} className="flex items-start gap-2 py-2 text-sm" data-testid={`ps-activity-${a.id}`}>
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800">{meta.label}</span>
                      <span className="text-xs text-slate-500">{a.by || "system"}</span>
                      <span className="text-xs text-slate-400">{a.at ? formatDateTime(a.at) : ""}</span>
                    </div>
                    <div className="text-xs text-slate-600">
                      {renderActivityDetails(a, user)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

// Activity rendering helpers — pulled out of the component to keep render
// shallow. Maps backend audit types to icons + plain-English summaries.
const ACTIVITY_META = {
  spare_part_added:                  { icon: Plus,        color: "text-blue-600",     label: "Spare part added" },
  spare_part_updated:                { icon: Pencil,      color: "text-blue-600",     label: "Spare part updated" },
  spare_part_deleted:                { icon: Trash2,      color: "text-rose-600",     label: "Spare part removed" },
  spare_parts_bulk_replace:          { icon: Wrench,      color: "text-indigo-600",   label: "Spare parts saved (bulk)" },
  additional_payment_link_sent:      { icon: CreditCard,  color: "text-emerald-600",  label: "Payment link sent" },
  additional_payment_link_cancelled: { icon: XCircle,     color: "text-rose-600",     label: "Payment link cancelled" },
  payment_status_override:           { icon: ActivityIcon, color: "text-amber-600",   label: "Payment status overridden" },
  package_changed:                   { icon: PackageIcon, color: "text-blue-600",     label: "Package changed" },
  quote_sent:                        { icon: CreditCard,  color: "text-emerald-600",  label: "Quote sent" },
  payment_link_sent:                 { icon: CreditCard,  color: "text-emerald-600",  label: "Booking payment link sent" },
  payment_received:                  { icon: CreditCard,  color: "text-emerald-700",  label: "Payment received" },
  invoice_generated:                 { icon: ActivityIcon, color: "text-purple-600",  label: "Invoice generated" },
  invoice_sent:                      { icon: ActivityIcon, color: "text-purple-700",  label: "Invoice sent" },
};

function renderActivityDetails(a, user) {
  const parts = [];
  if (a.part) parts.push(`${a.part}${a.qty ? ` × ${a.qty}` : ""}`);
  if (a.amount != null) parts.push(formatCurrency(a.amount, user));
  if (a.mode) parts.push(`(${a.mode})`);
  if (a.from && a.to) parts.push(`${a.from} → ${a.to}`);
  if (a.count != null) parts.push(`${a.count} items`);
  if (a.changes && Array.isArray(a.changes)) parts.push(`fields: ${a.changes.join(", ")}`);
  if (a.note) parts.push(`note: "${a.note}"`);
  return parts.join(" · ");
}
import { activeCurrencySymbol } from '@/utils/currency';
